import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PersonIcon from '@mui/icons-material/Person';
import { registerUser } from '../../utils/api';
import { useAuth } from '../../utils/authUtils';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../utils/LanguageContext';

const SignUp = ({ onToggleForm, redirectTo = '/app' }) => {
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const { language } = useLanguage();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const content = {
    en: {
      title: 'Create Account',
      subtitle: 'Join RumahKira to save your estimates',
      nameLabel: 'Full Name',
      emailLabel: 'Email Address',
      passwordLabel: 'Password',
      confirmPasswordLabel: 'Confirm Password',
      signUpBtn: 'Create Account',
      hasAccount: 'Already have an account?',
      signIn: 'Sign in',
      terms: 'By signing up, you agree to our Terms of Service',
      nameRequired: 'Name is required',
      emailRequired: 'Email is required',
      validEmail: 'Please enter a valid email',
      passwordRequired: 'Password is required',
      passwordMin: 'Password must be at least 6 characters',
      passwordMatch: 'Passwords do not match',
      registrationFailed: 'Registration failed. Please try again.',
    },
    my: {
      title: 'Cipta Akaun',
      subtitle: 'Sertai RumahKira untuk simpan anggaran anda',
      nameLabel: 'Nama Penuh',
      emailLabel: 'Alamat Emel',
      passwordLabel: 'Kata Laluan',
      confirmPasswordLabel: 'Sahkan Kata Laluan',
      signUpBtn: 'Cipta Akaun',
      hasAccount: 'Sudah ada akaun?',
      signIn: 'Log masuk',
      terms: 'Dengan mendaftar, anda bersetuju dengan Syarat Perkhidmatan kami',
      nameRequired: 'Nama diperlukan',
      emailRequired: 'Emel diperlukan',
      validEmail: 'Sila masukkan emel yang sah',
      passwordRequired: 'Kata laluan diperlukan',
      passwordMin: 'Kata laluan mesti sekurang-kurangnya 6 aksara',
      passwordMatch: 'Kata laluan tidak sepadan',
      registrationFailed: 'Pendaftaran gagal. Sila cuba lagi.',
    }
  };

  const t = content[language] || content.en;

  const validateForm = () => {
    if (!name.trim()) {
      setError(t.nameRequired);
      return false;
    }
    if (!email.trim()) {
      setError(t.emailRequired);
      return false;
    }
    if (!email.includes('@')) {
      setError(t.validEmail);
      return false;
    }
    if (!password) {
      setError(t.passwordRequired);
      return false;
    }
    if (password.length < 6) {
      setError(t.passwordMin);
      return false;
    }
    if (password !== confirmPassword) {
      setError(t.passwordMatch);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await registerUser(name.trim(), email.trim().toLowerCase(), password);
      await refreshUser();
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err.message || t.registrationFailed);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        {t.title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t.subtitle}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <TextField
          fullWidth
          label={t.nameLabel}
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoComplete="name"
          autoFocus
          margin="normal"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PersonIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          label={t.emailLabel}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          margin="normal"
          size="small"
        />

        <TextField
          fullWidth
          label={t.passwordLabel}
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          margin="normal"
          size="small"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                  size="small"
                >
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          label={t.confirmPasswordLabel}
          type={showPassword ? 'text' : 'password'}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          autoComplete="new-password"
          margin="normal"
          size="small"
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading}
          sx={{ mt: 3, mb: 2 }}
        >
          {loading ? <CircularProgress size={22} color="inherit" /> : t.signUpBtn}
        </Button>
      </Box>

      <Typography variant="caption" display="block" align="center" color="text.secondary" sx={{ mb: 2 }}>
        {t.terms}
      </Typography>

      <Divider sx={{ my: 2 }} />

      <Typography variant="body2" align="center">
        {t.hasAccount}{' '}
        <Typography
          component="span"
          variant="body2"
          color="primary"
          sx={{ cursor: 'pointer', fontWeight: 500 }}
          onClick={onToggleForm}
        >
          {t.signIn}
        </Typography>
      </Typography>
    </Box>
  );
};

export default SignUp;
